import type { DashboardMessageContact } from "../../messages.types.js";
import type { DashboardThread } from "../../types.js";
import type {
  DashboardMessageAttachmentRecordDto,
  DashboardMessageContactRecordDto,
  DashboardMessagesSeedDto,
  DashboardThreadMessageRecordDto,
  DashboardThreadParticipantRecordDto,
  DashboardThreadRecordDto,
} from "../types/messages.api";

export function cloneDashboardThread(thread: DashboardThread): DashboardThread {
  return {
    ...thread,
    participants: thread.participants.map((participant) => ({ ...participant })),
    messages: thread.messages.map((message) => ({
      ...message,
      attachment: message.attachment ? { ...message.attachment } : undefined,
      attachments: message.attachments?.map((attachment) => ({ ...attachment })),
    })),
    attachment: thread.attachment ? { ...thread.attachment } : undefined,
  };
}

export function cloneDashboardThreads(
  threads: ReadonlyArray<DashboardThread>,
): DashboardThread[] {
  return threads.map(cloneDashboardThread);
}

function mapAttachmentRecordToViewModel(
  attachment: DashboardMessageAttachmentRecordDto,
) {
  return {
    id: attachment.id,
    kind: attachment.kind,
    name: attachment.name,
    sizeLabel: attachment.sizeLabel,
    updatedAtLabel: attachment.updatedAtLabel,
    metaLabel: attachment.metaLabel,
  };
}

function mapMessageRecordToViewModel(message: DashboardThreadMessageRecordDto) {
  return {
    id: message.id,
    senderId: message.senderId,
    sender: message.sender,
    sentAtLabel: message.sentAtLabel,
    sentAtIso: message.sentAtIso,
    body: message.body,
    direction: message.direction,
    status: message.status,
    attachment: message.attachment
      ? mapAttachmentRecordToViewModel(message.attachment)
      : undefined,
    attachments: message.attachments?.map(mapAttachmentRecordToViewModel),
  };
}

function mapParticipantRecordToViewModel(
  participant: DashboardThreadParticipantRecordDto,
) {
  return {
    id: participant.id,
    name: participant.name,
    avatarLabel: participant.avatarLabel,
    accent: participant.accent,
    presence: participant.presence,
    roleLabel: participant.roleLabel,
    isCurrentUser: participant.isCurrentUser,
  };
}

export function mapDashboardThreadRecordToViewModel(
  thread: DashboardThreadRecordDto,
): DashboardThread {
  return {
    id: thread.id,
    title: thread.title,
    snippet: thread.snippet,
    updatedAtLabel: thread.updatedAtLabel,
    updatedAtIso: thread.updatedAtIso,
    participantsLabel: thread.participantsLabel,
    contextLabel: thread.contextLabel,
    participants: thread.participants.map(mapParticipantRecordToViewModel),
    messages: thread.messages.map(mapMessageRecordToViewModel),
    attachment: thread.attachment
      ? mapAttachmentRecordToViewModel(thread.attachment)
      : undefined,
    unreadCount: thread.unreadCount,
    isPinned: thread.isPinned,
  };
}

export function mapDashboardMessageContactRecordToViewModel(
  contact: DashboardMessageContactRecordDto,
): DashboardMessageContact {
  return {
    id: contact.id,
    name: contact.name,
    email: contact.email,
    avatarLabel: contact.avatarLabel,
    accent: contact.accent,
    presence: contact.presence,
    roleLabel: contact.roleLabel,
    companyLabel: contact.companyLabel,
    existingThreadId: contact.existingThreadId,
  };
}

export function mapDashboardMessagesSeedToViewModel(seed: DashboardMessagesSeedDto): {
  threads: DashboardThread[];
  contacts: DashboardMessageContact[];
} {
  return {
    threads: seed.threads.map(mapDashboardThreadRecordToViewModel),
    contacts: seed.contacts.map(mapDashboardMessageContactRecordToViewModel),
  };
}

function mapAttachmentToRecord(attachment: {
  id: string;
  kind: DashboardMessageAttachmentRecordDto["kind"];
  name: string;
  sizeLabel: string;
  updatedAtLabel: string;
  metaLabel?: string;
}): DashboardMessageAttachmentRecordDto {
  return {
    id: attachment.id,
    kind: attachment.kind,
    name: attachment.name,
    sizeLabel: attachment.sizeLabel,
    updatedAtLabel: attachment.updatedAtLabel,
    metaLabel: attachment.metaLabel,
  };
}

function mapMessageToRecord(message: DashboardThread["messages"][number]) {
  return {
    id: message.id,
    senderId: message.senderId,
    sender: message.sender,
    sentAtLabel: message.sentAtLabel,
    sentAtIso: message.sentAtIso,
    body: message.body,
    direction: message.direction,
    status: message.status,
    attachment: message.attachment
      ? mapAttachmentToRecord(message.attachment)
      : undefined,
    attachments: message.attachments?.map(mapAttachmentToRecord),
  } satisfies DashboardThreadMessageRecordDto;
}

function mapParticipantToRecord(
  participant: DashboardThread["participants"][number],
) {
  return {
    id: participant.id,
    name: participant.name,
    avatarLabel: participant.avatarLabel,
    accent: participant.accent,
    presence: participant.presence,
    roleLabel: participant.roleLabel,
    isCurrentUser: participant.isCurrentUser,
  } satisfies DashboardThreadParticipantRecordDto;
}

export function mapDashboardThreadToRecord(
  thread: DashboardThread,
): DashboardThreadRecordDto {
  return {
    id: thread.id,
    title: thread.title,
    snippet: thread.snippet,
    updatedAtLabel: thread.updatedAtLabel,
    updatedAtIso: thread.updatedAtIso,
    participantsLabel: thread.participantsLabel,
    contextLabel: thread.contextLabel,
    participants: thread.participants.map(mapParticipantToRecord),
    messages: thread.messages.map(mapMessageToRecord),
    attachment: thread.attachment
      ? mapAttachmentToRecord(thread.attachment)
      : undefined,
    unreadCount: thread.unreadCount,
    isPinned: thread.isPinned,
  };
}

export function mapDashboardMessageContactToRecord(
  contact: DashboardMessageContact,
): DashboardMessageContactRecordDto {
  return {
    id: contact.id,
    name: contact.name,
    email: contact.email,
    avatarLabel: contact.avatarLabel,
    accent: contact.accent,
    presence: contact.presence,
    roleLabel: contact.roleLabel,
    companyLabel: contact.companyLabel,
    existingThreadId: contact.existingThreadId,
  };
}
